import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";

/* Invite a second technician to WATCH this console session.
 *
 * Watchers are read-only — they see the device's screen, not the operator's
 * keystrokes, and have no path back into the session. That is enforced on
 * the server (the observer socket runs only the send loop); this component
 * says so plainly, because a colleague watching your screen should never be
 * a surprise to either party.
 *
 * The invitee list comes from the server's own "who could watch this"
 * endpoint rather than the user directory, so the picker cannot offer
 * somebody the invite would then refuse.
 */

type Invitable = { id: number; username: string; role: string };
type Invite = {
  id: number;
  invited_user_id: number;
  invited_username?: string | null;
  expires_at?: string | null;
  accepted_at?: string | null;
  revoked_at?: string | null;
  watching_now?: boolean;
};

export default function ConsoleInvitePanel({
  kind,
  sessionToken,
  compact = false,
}: {
  kind: "shell" | "screenshare";
  sessionToken: string;
  /** Toolbar button instead of a full card. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<Invitable[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  const base = `/api/v1/console/${kind}/${encodeURIComponent(sessionToken)}`;

  const refresh = useCallback(async () => {
    try {
      const [p, i] = await Promise.all([
        api.get(`${base}/invitable`),
        api.get(`${base}/invites`),
      ]);
      setPeople(p.data || []);
      setInvites(i.data || []);
      setErr("");
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't load who can be invited.");
    }
  }, [base]);

  useEffect(() => {
    if (!open) return;
    refresh();
    // While the panel is open, keep the "watching now" flag honest. An
    // operator glancing at a stale "nobody is watching" is the one thing
    // this panel must not do.
    const t = window.setInterval(refresh, 5000);
    return () => window.clearInterval(t);
  }, [open, refresh]);

  const invite = async (userId: number, username: string) => {
    setBusy(true);
    setErr("");
    setNote("");
    try {
      await api.post(`${base}/invites`, { user_id: userId });
      setNote(`${username} can now join. Send them the link from their AeroGauge console.`);
      await refresh();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't send that invite.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (inviteId: number) => {
    setBusy(true);
    setErr("");
    setNote("");
    try {
      await api.delete(`/api/v1/console/invites/${inviteId}`);
      await refresh();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't revoke that invite.");
    } finally {
      setBusy(false);
    }
  };

  const live = invites.filter((i) => !i.revoked_at);
  const watching = live.filter((i) => i.watching_now).length;
  const watchUrl = `${window.location.origin}/console/${kind}/${encodeURIComponent(sessionToken)}/watch`;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Invite another technician to watch this session"
        className={`text-[11px] px-2 py-1 rounded whitespace-nowrap ${
          open ? "bg-blue-600 hover:bg-blue-500" : "bg-gray-700 hover:bg-gray-600"
        }`}
      >
        {/* The count is the point: an unseen watcher is the failure mode. */}
        👥 {watching > 0 ? `${watching} watching` : compact ? "Invite" : "Invite someone"}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-80 z-50 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            They see this device's screen, <strong>read-only</strong> — they cannot type or
            click, and they cannot see what you type except as it appears on screen. The
            invite expires in 30 minutes and you can revoke it at any time.
          </div>

          {err && <div className="text-xs text-red-600 dark:text-red-400">{err}</div>}
          {note && <div className="text-xs text-green-700 dark:text-green-400">{note}</div>}

          {live.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Invited</div>
              {live.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    {i.invited_username}
                    {i.watching_now ? (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        watching
                      </span>
                    ) : i.accepted_at ? (
                      <span className="ml-1 text-[10px] text-gray-400">joined earlier</span>
                    ) : (
                      <span className="ml-1 text-[10px] text-gray-400">not yet joined</span>
                    )}
                  </span>
                  <button
                    onClick={() => revoke(i.id)}
                    disabled={busy}
                    className="text-[11px] text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    Revoke
                  </button>
                </div>
              ))}
              <button
                onClick={() => navigator.clipboard?.writeText(watchUrl)}
                className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                title={watchUrl}
              >
                Copy the link to send them
              </button>
              <p className="text-[10px] text-gray-400">
                The link only works for the people invited above — it is not a shareable
                password.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-400">
              Who can be invited
            </div>
            {people.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Nobody else has console access to this customer.
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {people
                  .filter((p) => !live.some((i) => i.invited_user_id === p.id))
                  .map((p) => (
                    <button
                      key={p.id}
                      onClick={() => invite(p.id, p.username)}
                      disabled={busy}
                      className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/50 disabled:opacity-50 flex items-center justify-between"
                    >
                      <span className="truncate">{p.username}</span>
                      <span className="text-[10px] text-gray-400">{p.role}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
